import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    updateStep, updateInstruction, fetchInstructions
} from '../configApi';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from 'shared/components/ui/dialog';
import {
    cn
} from 'shared/lib/utils';
import {
    Input
} from 'shared/components/ui/input';
import {
    Separator
} from 'shared/components/ui/separator';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
    Brain, Search, Globe, Database, GitBranch, RefreshCw, Workflow, Clock,
    CheckCircle2, Pencil, ChevronDown, ChevronRight, Eye, Settings, Info
} from 'lucide-react';

// A-0014: V2-only step renderer. The V1 fallback chain (`step.type`,
// `step.input_params.*`, `non-llm` kind) has been removed — post-A-0006 cleanup
// dev Redis is V2-only. Stgprod still has V1 opcodes (A-0008.09 + stgprod-cleanup
// owns that); deploy gating handled at the action level, not here.
const STEP_META = {
    llm:      {
        icon: Brain,
        color: 'text-violet-600 bg-violet-100 dark:text-violet-400 dark:bg-violet-900/40',
        label: 'LLM'
    },
    rag:      {
        icon: Search,
        color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/40',
        label: 'RAG'
    },
    api:      {
        icon: Globe,
        color: 'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/40',
        label: 'API'
    },
    memory:   {
        icon: Database,
        color: 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/40',
        label: 'Memory'
    },
    subflow:  {
        icon: Workflow,
        color: 'text-cyan-600 bg-cyan-100 dark:text-cyan-400 dark:bg-cyan-900/40',
        label: 'Subflow'
    },
    choose:   {
        icon: GitBranch,
        color: 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/40',
        label: 'Choose'
    },
    tool:     {
        icon: Clock,
        color: 'text-fuchsia-600 bg-fuchsia-100 dark:text-fuchsia-400 dark:bg-fuchsia-900/40',
        label: 'Tool'
    },
    parallel: {
        icon: RefreshCw,
        color: 'text-pink-600 bg-pink-100 dark:text-pink-400 dark:bg-pink-900/40',
        label: 'Parallel'
    },
    call:     {
        icon: Workflow,
        color: 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/40',
        label: 'Call'
    },
};

const DEFAULT_META = {
    icon: Workflow,
    color: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/40',
    label: 'Step'
};

// V2 step kinds for which we ship inline editors in V1 of A-0014.
// Other kinds (subflow/choose/tool/parallel/call) render as read-only summaries.
const EDITABLE_KINDS = new Set(['llm', 'rag', 'api', 'memory']);

function getStepType(step) {
    return (step?.kind || '').toLowerCase() || 'unknown';
}

function getStepName(step) {
    // V2 LLM: `unique_name` is at step root (per A-0008.03 §2.1 LLMStep schema).
    if (step?.unique_name) return step.unique_name.replace(/_/g, ' ');
    // V2 API: api_id is at step root.
    if (step?.api_id) return step.api_id;
    // V2 Subflow / Tool / Call refs.
    if (step?.subflow?.ref) return step.subflow.ref;
    if (step?.tool_ref?.tool_name) return step.tool_ref.tool_name;
    if (step?.call?.ref) return step.call.ref;
    return step?.id || 'Step';
}

function getStepDescription(step, resolveCollection = (id) => id, resolveApi = (id) => id) {
    const kind = getStepType(step);

    if (kind === 'llm') {
        const prompt = step.inputs?.query || '';
        if (prompt) {
            const cleaned = String(prompt).replace(/\s+/g, ' ').trim();
            return cleaned.length > 120 ? cleaned.slice(0, 120) + '…' : cleaned;
        }
    }
    if (kind === 'rag') {
        const cid = step.inputs?.collection_id;
        const op = step.inputs?.operation;
        const parts = [];
        if (op) parts.push(`op: ${op}`);
        if (cid) parts.push(`Collection: ${resolveCollection(cid)}`);
        return parts.join(' · ');
    }
    if (kind === 'api') {
        const apiId = step.api_id || '';
        return apiId ? resolveApi(apiId) : '';
    }
    if (kind === 'memory') {
        const mode = step.mode || step.format || '';
        return mode ? `mode: ${mode}` : '';
    }
    if (kind === 'subflow') return step.subflow?.ref ? `Ref: ${step.subflow.ref}` : '';
    if (kind === 'choose') return Array.isArray(step.cases) ? `${step.cases.length} case${step.cases.length === 1 ? '' : 's'}` : '';
    if (kind === 'tool') {
        const proto = step.protocol || (step.tool_ref ? 'mcp' : '');
        return proto ? `protocol: ${proto}` : '';
    }
    if (kind === 'parallel') return Array.isArray(step.branches) ? `${step.branches.length} branch${step.branches.length === 1 ? '' : 'es'}` : '';
    if (kind === 'call') return step.call?.ref ? `Ref: ${step.call.ref}` : '';
    return '';
}

function isEditable(step) {
    return EDITABLE_KINDS.has(getStepType(step));
}

function getStepDetails(step, resolveCollection = (id) => id, resolveApi = (id) => id) {
    const kind = getStepType(step);
    const details = [];

    if (kind === 'llm') {
        if (step.model_type) details.push({
            label: 'Model Type',
            value: step.model_type
        });
        if (step.unique_name) details.push({
            label: 'Instruction',
            value: step.unique_name
        });
        if (step.output_type) details.push({
            label: 'Output Type',
            value: step.output_type
        });
        if (step.stream) details.push({
            label: 'Stream',
            value: 'true'
        });
    }
    if (kind === 'rag') {
        if (step.inputs?.operation) details.push({
            label: 'Operation',
            value: step.inputs.operation
        });
        if (step.inputs?.collection_id) details.push({
            label: 'Collection',
            value: resolveCollection(step.inputs.collection_id)
        });
        if (step.inputs?.top_k !== undefined) details.push({
            label: 'Top K',
            value: String(step.inputs.top_k)
        });
        if (step.inputs?.use_rerank !== undefined) details.push({
            label: 'Rerank',
            value: String(step.inputs.use_rerank)
        });
    }
    if (kind === 'api') {
        if (step.api_id) details.push({
            label: 'API',
            value: resolveApi(step.api_id)
        });
        if (step.inputs?.request_timeout_s !== undefined) details.push({
            label: 'Timeout (s)',
            value: String(step.inputs.request_timeout_s)
        });
    }
    if (kind === 'memory') {
        if (step.mode) details.push({
            label: 'Mode',
            value: step.mode
        });
        if (step.turns !== undefined) details.push({
            label: 'Turns',
            value: String(step.turns)
        });
        if (step.max_tokens !== undefined) details.push({
            label: 'Max tokens',
            value: String(step.max_tokens)
        });
        if (step.format) details.push({
            label: 'Format',
            value: step.format
        });
    }
    if (kind === 'subflow') {
        if (step.subflow?.ref) details.push({
            label: 'Subflow Ref',
            value: step.subflow.ref
        });
    }
    if (kind === 'choose') {
        if (Array.isArray(step.cases)) details.push({
            label: 'Cases',
            value: String(step.cases.length)
        });
    }
    if (kind === 'tool') {
        if (step.protocol) details.push({
            label: 'Protocol',
            value: step.protocol
        });
        if (step.tool_ref?.connection_id) details.push({
            label: 'Connection',
            value: step.tool_ref.connection_id
        });
        if (step.tool_ref?.tool_name) details.push({
            label: 'Tool',
            value: step.tool_ref.tool_name
        });
    }
    if (kind === 'parallel') {
        if (Array.isArray(step.branches)) details.push({
            label: 'Branches',
            value: String(step.branches.length)
        });
    }
    if (kind === 'call') {
        if (step.call?.ref) details.push({
            label: 'Definition Ref',
            value: step.call.ref
        });
    }

    // Step-common fields (A-0008.03 step-common: description, role, timeout_ms, on_error).
    if (step.timeout_ms !== undefined) details.push({
        label: 'Timeout (ms)',
        value: String(step.timeout_ms)
    });
    if (step.role) details.push({
        label: 'Role',
        value: step.role
    });
    if (step.on_error?.action) details.push({
        label: 'On error',
        value: step.on_error.action
    });

    return details;
}

// A-0014 — V2 prompt accessor (used by template-field parser in ManageInstructionDialog).
function getPrompt(step) {
    return step?.inputs?.query || '';
}

// A-0014 — V2 RAG query-text accessor.
// V2 RAG uses `inputs.query` for basic_search / search_with_files variants
// (see redisJSONOpcodeSchema RagInputs*); kept name `getQueryText` for in-file
// callsites that haven't been renamed.
function getQueryText(step) {
    return step?.inputs?.query_text || step?.inputs?.query || '';
}

function normalizeResourceRef(value) {
    return typeof value === 'string' ? value.trim().replace(/^['"]|['"]$/g, '') : value;
}

function groupStepsByOpcode(steps) {
    const groups = [];
    const groupMap = new Map();

    for (const step of steps) {
        const opcodeId = step._opcode_id || 'unknown';
        const opcodeName = step._opcode_name || 'Unknown Opcode';

        if (!groupMap.has(opcodeId)) {
            const group = {
                opcodeId,
                opcodeName,
                steps: []
            };
            groupMap.set(opcodeId, group);
            groups.push(group);
        }
        groupMap.get(opcodeId).steps.push(step);
    }

    return groups;
}

export default function InstructionConfig({
    instructions: initialSteps, collections = [], apis = [], instanceId, configKey
}) {
    const [steps, setSteps] = useState(initialSteps);

    const collectionNameMap = useMemo(() => {
        const map = {};
        for (const c of collections) {
            map[c._id] = c.name || c.title || c._id;
        }
        return map;
    }, [collections]);

    const apiNameMap = useMemo(() => {
        const map = {};
        for (const a of apis) {
            map[a._id] = a.name || a.title || a._id;
        }
        return map;
    }, [apis]);

    const getCollectionName = (id) => {
        const normalized = normalizeResourceRef(id);
        return collectionNameMap[normalized] || normalized || id;
    };
    const getApiName = (id) => apiNameMap[id] || id;
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [editingId, setEditingId] = useState(null);
    const [editPrompt, setEditPrompt] = useState('');
    const [editQueryText, setEditQueryText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [collapsedOpcodes, setCollapsedOpcodes] = useState(new Set());
    const [manageStep, setManageStep] = useState(null);

    const opcodeGroups = useMemo(() => groupStepsByOpcode(steps || []), [steps]);

    const toggleOpcodeCollapse = (opcodeId) => {
        setCollapsedOpcodes(prev => {
            const next = new Set(prev);
            if (next.has(opcodeId)) {
                next.delete(opcodeId);
            } else {
                next.add(opcodeId);
            }
            return next;
        });
    };

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                setEditingId(null);
            } else {
                next.add(id);
            }
            return next;
        });
        setError(null);
    };

    const startEdit = (step) => {
        const id = step.id;
        setEditingId(id);
        setExpandedIds(prev => new Set(prev).add(id));
        // A-0014 — seed the editor with V2 inputs only (no V1 fallback chain).
        // For LLM: inputs.query. For RAG: inputs.query (or inputs.query_text per operation).
        setEditPrompt(step.inputs?.query || '');
        setEditQueryText(step.inputs?.query_text || step.inputs?.query || '');
        setError(null);
        setSuccess(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setError(null);
    };

    const handleSave = async (step) => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const stepType = getStepType(step);
            // A-0014 — V2 step partial body. Server validates against
            // stepPartials[step.kind]. Unknown fields rejected.
            const updates = {
                inputs: {}
            };

            if (stepType === 'llm') {
                updates.inputs.query = editPrompt;
            } else if (stepType === 'rag') {
                // V2 RAG uses `inputs.query` (basic_search / search_with_files) — see
                // redisJSONOpcodeSchema.js RagInputsBasicSearch / RagInputsSearchWithFiles.
                updates.inputs.query = editQueryText;
            } else if (stepType === 'api') {
                // No inline body editing for API in V1 of A-0014; keep as no-op.
                setEditingId(null);
                setSaving(false);
                return;
            } else if (stepType === 'memory') {
                // Memory step common toggle is done elsewhere; in-row edit is a no-op for now.
                setEditingId(null);
                setSaving(false);
                return;
            }

            await updateStep(instanceId, configKey, {
                opcode_id: step._opcode_id,
                step_id: step.id,
                updates,
            });

            // Update local state — V2-only paths.
            setSteps(prev => prev.map(s => {
                if (s.id !== step.id) return s;
                const updated = {
                    ...s,
                    inputs: {
                        ...(s.inputs || {})
                    }
                };
                if (stepType === 'llm') {
                    updated.inputs.query = editPrompt;
                } else if (stepType === 'rag') {
                    updated.inputs.query = editQueryText;
                }
                return updated;
            }));

            setSuccess(step.id);
            setEditingId(null);
            setTimeout(() => setSuccess(null), 3000);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    if (!steps || steps.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="h-14 w-14 rounded-full bg-[#b1092a]/10 flex items-center justify-center mb-4">
                    <Workflow className="h-7 w-7 text-[#b1092a]" />
                </div>
                <h3 className="text-base font-semibold mb-1">No opcode steps found</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                    Opcodes define the processing logic your assistant follows. Steps within opcodes control how queries are handled.
                </p>
            </div>
        );
    }

    const handleManageSave = async (step, updatedInstruction) => {
        setSaving(true);
        setError(null);
        try {
            // A-0014 — V2 step shape: unique_name / model_type / modelId at step root.
            const data = updatedInstruction.instructionData || {};
            const instructionName = data.unique_name || step.unique_name || '';
            const modelType = data.model_type || step.model_type || '';
            const modelId = data.modelId || step.modelId || '';
            const instructionId = data._id || step._id || '';

            await updateInstruction(instanceId, configKey, {
                unique_name: instructionName,
                model_type: modelType,
                modelId,
                instructionId,
                prompt: updatedInstruction.prompt,
                modelDescription: updatedInstruction.modelDescription,
            });

            // Update local step state to reflect the new prompt — V2 shape only.
            setSteps(prev => prev.map(s => {
                if (s.id !== step.id) return s;
                return {
                    ...s,
                    inputs: {
                        ...(s.inputs || {}),
                        query: updatedInstruction.prompt
                    },
                };
            }));

            setSuccess(step.id);
            setManageStep(null);
            setTimeout(() => setSuccess(null), 3000);
        } catch (e) {
            throw e;
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-2xl space-y-6">
            <ManageInstructionDialog
                step={manageStep}
                onClose={() => setManageStep(null)}
                onSave={handleManageSave}
                instanceId={instanceId}
                configKey={configKey}
            />
            {opcodeGroups.map((group) => {
                const isCollapsed = collapsedOpcodes.has(group.opcodeId);

                return (
                    <div key={group.opcodeId}>
                        {/* Opcode header */}
                        <button
                            className="w-full flex items-center gap-2 px-1 py-2 mb-3 text-left cursor-pointer group"
                            onClick={() => toggleOpcodeCollapse(group.opcodeId)}
                        >
                            {isCollapsed
                                ? <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            }
                            <Workflow className="h-4 w-4 text-primary" />
                            <span className="font-semibold text-sm">{group.opcodeName}</span>
                            <span className="text-xs text-muted-foreground ml-1">
                                {group.steps.length} {group.steps.length === 1 ? 'step' : 'steps'}
                            </span>
                        </button>

                        {/* Steps timeline */}
                        {!isCollapsed && (
                            <div className="ml-3">
                                {group.steps.map((step, idx) => {
                                    const id = step.id || `step-${group.opcodeId}-${idx}`;
                                    const stepType = getStepType(step);
                                    const meta = STEP_META[stepType] || DEFAULT_META;
                                    const Icon = meta.icon;
                                    const isExpanded = expandedIds.has(id);
                                    const isEditing = editingId === id;
                                    const name = getStepName(step);
                                    const description = getStepDescription(step, getCollectionName, getApiName);
                                    const editable = isEditable(step);
                                    // A-0014 — V2 step kinds. `non-llm` is dead in V2 (post-A-0006 cleanup);
                                    // RAG keeps its inline query editor under the same UI affordance previously
                                    // labeled "Query Text".
                                    const isLLM = stepType === 'llm';
                                    const isRAG = stepType === 'rag';
                                    const isReadOnlySummary = !EDITABLE_KINDS.has(stepType);

                                    return (
                                        <div key={id} className="flex gap-3">
                                            {/* Timeline */}
                                            <div className="flex flex-col items-center shrink-0 w-8">
                                                <div className={cn('flex items-center justify-center w-8 h-8 rounded-full shrink-0 text-xs font-bold', meta.color)}>
                                                    {idx + 1}
                                                </div>
                                                {idx < group.steps.length - 1 && (
                                                    <div className="w-px flex-1 bg-border min-h-[16px]" />
                                                )}
                                            </div>

                                            {/* Card */}
                                            <Card className={cn('flex-1 mb-3 transition-shadow', isExpanded && 'shadow-md')}>
                                                <button
                                                    className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer"
                                                    onClick={() => toggleExpand(id)}
                                                >
                                                    <div className={cn('flex items-center justify-center w-7 h-7 rounded-md shrink-0', meta.color)}>
                                                        <Icon className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-sm truncate">{name}</span>
                                                            <span className="text-xs text-muted-foreground shrink-0">{meta.label}</span>
                                                        </div>
                                                        {!isExpanded && description && (
                                                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{description}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        {success === id && <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3.5 w-3.5" /> Saved</span>}
                                                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                                    </div>
                                                </button>

                                                {isExpanded && (
                                                    <CardContent className="border-t pt-4 pb-4 space-y-3">
                                                        {/* Step details */}
                                                        {getStepDetails(step, getCollectionName, getApiName).map(({
                                                            label, value
                                                        }) => (
                                                            <div key={label} className="flex items-start gap-2">
                                                                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground min-w-[90px] shrink-0">{label}</span>
                                                                <span className="text-sm font-mono break-all">{value}</span>
                                                            </div>
                                                        ))}

                                                        {/* LLM prompt + manage button */}
                                                        {isLLM && (
                                                            <div className="space-y-1.5 mt-2">
                                                                <div className="flex items-center justify-between">
                                                                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prompt</Label>
                                                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs gap-1" onClick={(e) => { e.stopPropagation(); setManageStep(step); }}>
                                                                        <Settings className="h-3 w-3" /> Manage
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* A-0014 — RAG inline query editor (V2 inputs.query / inputs.query_text). */}
                                                        {isRAG && (
                                                            <div className="space-y-1.5 mt-2">
                                                                <div className="flex items-center justify-between">
                                                                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Query</Label>
                                                                    {!isEditing && (
                                                                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs gap-1" onClick={(e) => { e.stopPropagation(); startEdit(step); }}>
                                                                            <Pencil className="h-3 w-3" /> Edit
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                {isEditing ? (
                                                                    <Textarea
                                                                        rows={4}
                                                                        className="font-mono text-xs resize-y"
                                                                        value={editQueryText}
                                                                        onChange={e => setEditQueryText(e.target.value)}
                                                                    />
                                                                ) : (
                                                                    <pre className="text-xs bg-muted/50 rounded-md p-3 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono leading-relaxed">{getQueryText(step) || '(empty)'}</pre>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* A-0014 — read-only summary for V2 step kinds we don't yet inline-edit
                                                            (subflow / choose / tool / parallel / call). Tenants edit these in DE Studio. */}
                                                        {isReadOnlySummary && (
                                                            <div className="mt-2 rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 p-3 text-xs">
                                                                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                                                                    {meta.label} step (read-only)
                                                                </div>
                                                                <pre className="font-mono text-[11px] whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                                                                    {JSON.stringify({
                                                                        kind: step.kind,
                                                                        id: step.id,
                                                                        ...(step.subflow ? {
                                                                            subflow: step.subflow
                                                                        } : {}),
                                                                        ...(step.tool_ref ? {
                                                                            tool_ref: step.tool_ref,
                                                                            protocol: step.protocol
                                                                        } : {}),
                                                                        ...(step.cases ? {
                                                                            cases: step.cases
                                                                        } : {}),
                                                                        ...(step.branches ? {
                                                                            branches: step.branches.map(b => ({
                                                                                id: b.id,
                                                                                kind: b.kind
                                                                            }))
                                                                        } : {}),
                                                                        ...(step.call ? {
                                                                            call: step.call
                                                                        } : {}),
                                                                    }, null, 2)}
                                                                </pre>
                                                                <p className="text-muted-foreground/70 mt-1.5 text-[10px] italic">
                                                                    Inline editing not available — use DE Studio for complex step kinds.
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* A-0014 — RAG inline edit actions. */}
                                                        {isEditing && isRAG && (
                                                            <div className="space-y-2">
                                                                {error && <p className="text-sm text-destructive">{error}</p>}
                                                                <div className="flex justify-end gap-2">
                                                                    <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                                                                    <Button size="sm" onClick={() => handleSave(step)} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Non-editable notice */}
                                                        {!editable && (
                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded-md px-3 py-2 mt-2">
                                                                <Eye className="h-3.5 w-3.5" />
                                                                <span>This step is read-only.</span>
                                                            </div>
                                                        )}

                                                        {/* A-0014 — RAG inline edit actions. */}
                                                        {isEditing && isRAG && (
                                                            <div className="space-y-2">
                                                                {error && <p className="text-sm text-destructive">{error}</p>}
                                                                <div className="flex justify-end gap-2">
                                                                    <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                                                                    <Button size="sm" onClick={() => handleSave(step)} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                )}
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// Parse structured prompt (## Instruction / ## Context / etc.) into fields
function parsePromptFields(rawPrompt) {
    const fields = {
        instruction: '',
        context: '',
        targetFormat: '',
        sampleInput: '',
        sampleOutput: ''
    };
    if (!rawPrompt) return fields;

    const labels = ['## Instruction', '## Context', '## Target Format', '## Sample Input', '## Sample Output'];
    const fieldKeys = ['instruction', 'context', 'targetFormat', 'sampleInput', 'sampleOutput'];
    const lines = rawPrompt.split('\n');

    let currentIdx = -1;
    let currentLines = [];

    for (const line of lines) {
        const trimmed = line.trim();
        const labelIdx = labels.indexOf(trimmed);
        if (labelIdx !== -1) {
            if (currentIdx !== -1) {
                fields[fieldKeys[currentIdx]] = currentLines.join('\n').trim();
            }
            currentIdx = labelIdx;
            currentLines = [];
        } else if (currentIdx !== -1) {
            currentLines.push(line);
        }
    }
    if (currentIdx !== -1) {
        fields[fieldKeys[currentIdx]] = currentLines.join('\n').trim();
    }

    // If no structured fields found, treat the whole prompt as instruction
    if (currentIdx === -1) {
        fields.instruction = rawPrompt;
    }

    return fields;
}

function buildPrompt(fields) {
    let prompt = '';
    if (fields.instruction) prompt += `## Instruction\n${fields.instruction}`;
    if (fields.context) prompt += `\n\n## Context\n${fields.context}`;
    if (fields.targetFormat) prompt += `\n\n## Target Format\n${fields.targetFormat}`;
    if (fields.sampleInput) prompt += `\n\n## Sample Input\n${fields.sampleInput}`;
    if (fields.sampleOutput) prompt += `\n\n## Sample Output\n${fields.sampleOutput}`;
    return prompt;
}

const PROMPT_FIELD_CONFIG = [
    {
        key: 'instruction',
        label: 'Instruction',
        required: true,
        tooltip: 'Provide specific tasks or questions for the language model to address. This sets the purpose and direction for the response.'
    },
    {
        key: 'context',
        label: 'Context',
        required: false,
        tooltip: 'Supply background information or details that help the language model understand the scenario or topic, ensuring relevant and accurate responses.'
    },
    {
        key: 'targetFormat',
        label: 'Target Format',
        required: false,
        tooltip: 'Specify the structure and style of the response. This ensures that the output is consistent with your needs.'
    },
    {
        key: 'sampleInput',
        label: 'Sample Input',
        required: false,
        tooltip: 'Offer an example of what the input data might look like. This helps clarify the format and type of data expected.'
    },
    {
        key: 'sampleOutput',
        label: 'Sample Output',
        required: false,
        tooltip: 'Show a representative example of the desired response. This demonstrates the format and quality of output you are looking for.'
    },
];

function AutoResizeTextarea({
    value, onChange, rows = 2, ...props
}) {
    const ref = React.useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [value]);

    return (
        <Textarea
            ref={ref}
            value={value}
            onChange={onChange}
            rows={rows}
            style={{
                resize: 'none',
                overflow: 'hidden',
                minHeight: `${rows * 1.5}rem`
            }}
            {...props}
        />
    );
}

function ManageInstructionDialog({
    step, onClose, onSave, instanceId, configKey
}) {
    const [fields, setFields] = useState({
        instruction: '',
        context: '',
        targetFormat: '',
        sampleInput: '',
        sampleOutput: ''
    });
    const [description, setDescription] = useState('');
    const [instructionData, setInstructionData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const isOpen = !!step;

    // A-0014 — V2 step root fields.
    const instructionName = step?.unique_name || '';
    const modelType = step?.model_type || '';

    // Fetch the full instruction data when dialog opens
    useEffect(() => {
        if (!step || !instanceId || !configKey) return;

        setLoading(true);
        setError(null);
        setInstructionData(null);

        fetchInstructions(instanceId, configKey)
            .then((instructions) => {
                const match = instructions.find(
                    i => i.unique_name === instructionName && i.model_type === modelType
                );
                if (match) {
                    setInstructionData(match);
                    setFields(parsePromptFields(match.prompt || ''));
                    setDescription(match.modelDescription || '');
                } else {
                    // Fallback to step data if instruction not found
                    setFields(parsePromptFields(getPrompt(step)));
                    setDescription('');
                }
            })
            .catch((e) => {
                setError('Failed to load instruction: ' + e.message);
                // Fallback to step data
                setFields(parsePromptFields(getPrompt(step)));
            })
            .finally(() => setLoading(false));
    }, [step]);

    const handleOpenChange = (open) => {
        if (!open) onClose();
    };

    const handleFieldChange = (key, value) => {
        setFields(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        if (!fields.instruction.trim()) {
            setError('Please fill in all required fields: instruction');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave(step, {
                prompt: buildPrompt(fields),
                modelDescription: description,
                instructionData,
            });
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // A-0014 — V2 modelId at step root (no input_params wrapper).
    const modelId = instructionData?.modelId || step?.modelId || '';

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-[850px] w-full">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">Manage Instruction</DialogTitle>
                    <p className="text-md text-muted-foreground">Manage instruction data.</p>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    </div>
                ) : (
                    <React.Fragment>
                        {/* Model display (read-only) */}
                        <div className="flex items-center gap-2">
                            <div className="flex-1 flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm">
                                <span className="text-muted-foreground">{modelId || instructionName}</span>
                                {modelType && <span className="text-xs text-muted-foreground ml-auto">- ({modelType})</span>}
                            </div>
                        </div>

                        <Separator />

                        <ScrollArea className="max-h-[360px]">
                            <div className="space-y-2 px-1 pt-4">
                                {/* Unique Name */}
                                <div>
                                    <Label htmlFor="unique_name" className="text-right">Unique Name</Label>
                                    <Input
                                        id="unique_name"
                                        value={instructionName}
                                        disabled
                                        className="col-span-3"
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <Label htmlFor="description" className="text-right">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="col-span-3"
                                        placeholder="Enter description for your instruction"
                                    />
                                </div>
                            </div>

                            {/* Prompt fields (Anatomy-style) */}
                            <div className="flex flex-col gap-4 px-1 py-4">
                                {PROMPT_FIELD_CONFIG.map(({
                                    key, label, required, tooltip
                                }) => (
                                    <div key={key} className="flex flex-col gap-[2px]">
                                        <div className="flex flex-row items-center gap-2">
                                            <TooltipProvider>
                                                <Label className="select-none text-xs">
                                                    {label} {required && <span className="text-red-500">*</span>}
                                                </Label>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <Info className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="w-[300px]">
                                                        <p>{tooltip}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                        <div className="border rounded-md">
                                            <AutoResizeTextarea
                                                value={fields[key]}
                                                onChange={e => handleFieldChange(key, e.target.value)}
                                                rows={2}
                                                className="w-full p-2 text-xs border-none focus-visible:ring-0 shadow-none"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>

                        {error && <p className="text-sm text-destructive">{error}</p>}

                        <DialogFooter className="flex justify-end space-x-5 mt-6">
                            <Button variant="outline" onClick={onClose} className="px-5 py-3 size-md border-solid shadow-none text-accent-foreground">Cancel</Button>
                            <Button onClick={handleSave} disabled={saving} className="px-5 py-3 text-md">
                                {saving ? 'Updating instruction...' : 'Update instruction'}
                            </Button>
                        </DialogFooter>
                    </React.Fragment>
                )}
            </DialogContent>
        </Dialog>
    );
}
